"use client";
import { FlexBetween, FlexBetweenCol, FlexCol } from "@/components";
import { cn } from "@/lib";
import CustomButton from "../ui/customButton";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  IInquiriesComponentProps,
  TCompanyInformationInfer,
} from "@/interfaces";
import TextField from "../ui/textField";
import { useCompanyInfoInquiryMutation } from "@/hooks/useInquiry";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { companyIdentificationNumberSchema } from "@/validations";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { useModalStore } from "@/store";
import { landingQuickAccessNavigation } from "@/constants";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { useState } from "react";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { globalRawServicesApiUnification } from "@/constants/globalRawServicesData/globalRawServicesApiUnification";
import { ICompanyInfoInquiryMutationPayload } from "@/interfaces/inquiries/legal";

const CompanyInformation = ({
  openPaymentModal,
  isLoggedIn,
  navigateToLogin,
  isOpen,
  onClose,
}: IInquiriesComponentProps) => {
  const t = useTranslations();
  const { closeModal } = useModalStore();
  const { data: session } = useSession();
  const {
    control,
    handleSubmit,
    formState: { errors: formError },
    getValues,
  } = useForm<TCompanyInformationInfer>({
    resolver: zodResolver(companyIdentificationNumberSchema),
    defaultValues: {
      digit: "",
    },
  });
  const {
    isPending: isSubmittingCompanyInfo,
    mutate: companyInfoInquiryFn,
    error: companyInfoInquiryError,
    reset: resetCompanyInfoInquiry,
  } = useCompanyInfoInquiryMutation(session?.user.token);

  const router = useRouter();
  const [packageId, setPackageId] = useState<string | null>(null);
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };
  const onSubmit = (data: any) => {
    if (!isLoggedIn) {
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };
  const companyInfoInquiryDetails = landingQuickAccessNavigation.find(
    (item) =>
      item.key === globalRawServicesData.Company_Information_Inquiry.key,
  );

  const paymentSubmission = () => {
    const formData = getValues();
    const payload: Readonly<ICompanyInfoInquiryMutationPayload> = {
      national_code: formData.digit,
      campaign: packageId || undefined,
    };
    companyInfoInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
        },
      },
    );
  };

  return (
    <>
      <FlexBetween className="items-stretch gap-8 lg:gap-12">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn("flex-center basis-full md:basis-1/2")}
        >
          <FlexBetweenCol gap={6} className={cn("w-full xl:gap-8")}>
            <p className={cn("text-primary-main text-sm xl:text-base")}>
              {t("inquiries.companyInfo.formTitle")}
            </p>
            <Controller
              name="digit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  maxLength={11}
                  numeric
                  aria-label="national code"
                  hasShadow
                  inputMode="numeric"
                  placeholder={t("inquiries.companyInfo.placeholder")}
                  inputClassName={cn("tracking-widest dir-ltr h-10")}
                  error={formError.digit?.message}
                />
              )}
            />
            <CustomButton
              id="landing-company-information-submit"
              type="submit"
              className="mt-2 w-max lg:mt-4"
              onClick={handleSubmit(onSubmit)}
            >
              {t("inquiries.companyInfo.submitButton")}
            </CustomButton>
          </FlexBetweenCol>
        </form>
        <FlexCol
          gap={2}
          className={cn("hidden basis-1/2 sm:flex lg:gap-4 xl:gap-6")}
        >
          <h2 className={cn("text-sm font-bold lg:text-base xl:text-xl")}>
            {t("inquiries.companyInfo.headerTitle")}
          </h2>
          <p
            className={cn(
              "text-custom-gray-200 text-justify text-xs leading-5 whitespace-pre-line lg:text-sm lg:leading-normal xl:text-base",
            )}
          >
            {t("inquiries.companyInfo.description")}
          </p>
        </FlexCol>
      </FlexBetween>
      {isOpen && (
        <PaymentModalContainer
          payBtnId="landing-payment-company-information"
          submissionError={companyInfoInquiryError}
          paymentSubmission={paymentSubmission}
          isOpen={isOpen}
          onClose={onClose}
          showData="service"
          serviceId={
            globalRawServicesApiUnification.Company_Information_Inquiry.apiId
          }
          selectedService={companyInfoInquiryDetails}
          submissionLoading={isSubmittingCompanyInfo}
          onErrorReset={resetCompanyInfoInquiry}
          onPackageSelect={handlePackageSelect}
        />
      )}
    </>
  );
};

export default CompanyInformation;
